import {
    ManagedHostingProvider,
    ManagedHostingStatus,
    type ManagedHostingAction,
    type ManagedHostingBackup,
    type ManagedHostingDeployment,
    type ManagedHostingLocation,
    type ManagedHostingOperation,
    type ManagedHostingVideoRegion,
} from './types'

/** Semantic colors shared by both portals; the values match MUI's palette names. */
export type ManagedHostingTone = 'default' | 'success' | 'info' | 'warning' | 'error'

const {
    queued,
    provisioning,
    installing,
    ready,
    failed,
    deleting,
    deleteFailed,
    deleted,
    recoverable,
    restoring,
    purging,
    purgeFailed,
    stopped,
    degraded,
    upgrading,
    suspending,
} = ManagedHostingStatus

/** Statuses from which each lifecycle action may be requested. The billing server enforces the same table. */
export const managedHostingActionStatuses: Record<ManagedHostingAction, readonly ManagedHostingStatus[]> = {
    backup: [ready, degraded],
    'restore-backup': [ready, degraded],
    upgrade: [ready, stopped, degraded],
    restore: [recoverable],
    purge: [recoverable, failed, deleteFailed, purgeFailed],
    start: [stopped],
    stop: [ready, degraded],
    reconcile: [ready, stopped, degraded],
    retry: [failed, deleteFailed, purgeFailed, degraded],
    'set-power-policy': [ready, stopped, degraded],
}

/** Deleting a deployment in these statuses retains it for seven days; any other deletion cancels and cleans up. */
export const recoverableDeletionStatuses: readonly ManagedHostingStatus[] = [ready, stopped, degraded]

/** Deletion is already underway or complete in these statuses, so a new request would change nothing. */
const deletionStatuses: readonly ManagedHostingStatus[] = [deleted, deleting, suspending, recoverable, purging]

/** Statuses in which the worker is changing the deployment, so it must not be deleted yet. */
const busyStatuses: readonly ManagedHostingStatus[] = [restoring, upgrading]

/** Edge Connects keep no backups: their configuration and identity live in their Core. */
const kindActions: Partial<Record<ManagedHostingAction, ManagedHostingDeployment['kind']>> = {
    backup: 'core',
    'restore-backup': 'core',
    start: 'connect',
    stop: 'connect',
    'set-power-policy': 'connect',
}

export const managedHostingStatusLabels: Record<ManagedHostingStatus, string> = {
    [queued]: 'Queued',
    [provisioning]: 'Provisioning',
    [installing]: 'Installing',
    [ready]: 'Ready',
    [failed]: 'Failed',
    [deleting]: 'Deleting',
    [deleteFailed]: 'Deletion failed',
    [deleted]: 'Deleted',
    [recoverable]: 'Deleted, recoverable',
    [restoring]: 'Restoring',
    [purging]: 'Deleting permanently',
    [purgeFailed]: 'Permanent deletion failed',
    [stopped]: 'Stopped',
    [degraded]: 'Degraded',
    [upgrading]: 'Upgrading',
    [suspending]: 'Stopping for deletion',
}

const statusTones: Record<ManagedHostingStatus, ManagedHostingTone> = {
    [queued]: 'info',
    [provisioning]: 'info',
    [installing]: 'info',
    [ready]: 'success',
    [failed]: 'error',
    [deleting]: 'info',
    [deleteFailed]: 'error',
    [deleted]: 'default',
    [recoverable]: 'warning',
    [restoring]: 'info',
    [purging]: 'info',
    [purgeFailed]: 'error',
    [stopped]: 'default',
    [degraded]: 'warning',
    [upgrading]: 'info',
    [suspending]: 'info',
}

export const managedHostingStatusTone = (status: ManagedHostingStatus) => statusTones[status]

/**
 * How a deployment's message reads: the worker's latest progress while it sets the node up or changes it, a warning
 * beside a node that still runs, or the error that stopped it.
 */
export const managedHostingMessageSeverity = (status: ManagedHostingStatus): 'info' | 'warning' | 'error' => {
    const tone = statusTones[status]
    return tone === 'info' || tone === 'error' ? tone : 'warning'
}

export const managedHostingOperationLabels: Record<ManagedHostingOperation['action'], string> = {
    backup: 'Backup',
    'restore-backup': 'Restore from backup',
    upgrade: 'Upgrade',
    restore: 'Restore',
    purge: 'Permanent deletion',
    start: 'Start',
    stop: 'Stop',
    reconcile: 'Health check',
    retry: 'Retry',
    'set-power-policy': 'Power policy change',
    suspend: 'Deletion',
    'create-region': 'Video region creation',
    'delete-region': 'Video region removal',
}

/** Steps of a member's backup, restore or upgrade, and of group deletion and restoration. */
const operationStageLabels: Record<string, string> = {
    'backup-wait': 'Waiting for the backup to reach storage',
    'restore-pull': 'Fetching the backup',
    'restore-wait': 'Restoring the configuration',
    'restore-reapply': 'Reapplying hosting settings',
    'restore-reenroll': 'Reconnecting hosted nodes',
    'upgrade-start': 'Starting the node',
    'upgrade-backup': 'Backing up the configuration',
    'upgrade-submit': 'Requesting the installation',
    'upgrade-observe': 'Installing the release',
    'upgrade-commit': 'Confirming the new release',
    'upgrade-preparation-resume': 'Returning the node to its previous state',
    'upgrade-failed': 'Installation failed',
    'upgrade-preparation-failed': 'Stopped before installation',
    'suspend-members': "Stopping the group's nodes",
    'restore-members': "Starting the group's nodes",
    'purge-members': "Removing the group's nodes",
    'restore-group-network': 'Reconnecting private networking',
}

/**
 * What a running operation is doing, or where a failed one stopped. Queued and completed operations show none, and
 * neither do actions that run in a single step.
 */
export const managedHostingOperationStageLabel = (operation: Pick<ManagedHostingOperation, 'stage' | 'status'>) =>
    operation.status === 'running' || operation.status === 'failed' ? operationStageLabels[operation.stage] : undefined

export const managedHostingOperationStatusLabels: Record<ManagedHostingOperation['status'], string> = {
    queued: 'Queued',
    running: 'Running',
    succeeded: 'Succeeded',
    failed: 'Failed',
}

const operationStatusTones: Record<ManagedHostingOperation['status'], ManagedHostingTone> = {
    queued: 'info',
    running: 'info',
    succeeded: 'success',
    failed: 'error',
}

export const managedHostingOperationStatusTone = (status: ManagedHostingOperation['status']) =>
    operationStatusTones[status]

export const managedHostingBackupPurposeLabels: Record<ManagedHostingBackup['purpose'], string> = {
    scheduled: 'Scheduled',
    manual: 'Manual',
    upgrade: 'Before upgrade',
}

export const managedHostingVideoRegionStatusLabels: Record<ManagedHostingVideoRegion['status'], string> = {
    queued: 'Creating',
    ready: 'Ready',
    deleting: 'Removing',
    deleted: 'Removed',
    failed: 'Failed',
}

const videoRegionStatusTones: Record<ManagedHostingVideoRegion['status'], ManagedHostingTone> = {
    queued: 'info',
    ready: 'success',
    deleting: 'info',
    deleted: 'default',
    failed: 'error',
}

export const managedHostingVideoRegionStatusTone = (status: ManagedHostingVideoRegion['status']) =>
    videoRegionStatusTones[status]

const powerStateLabels: Record<string, string> = {
    running: 'Running',
    starting: 'Starting',
    stopping: 'Stopping',
    stopped: 'Stopped',
    missing: 'Missing',
}

/** "Running · automatic power" for an Edge Connect. */
export const managedHostingPowerLabel = (deployment: Pick<ManagedHostingDeployment, 'powerState' | 'powerPolicy'>) =>
    `${powerStateLabels[deployment.powerState ?? ''] ?? 'Unknown'} · ${deployment.powerPolicy === 'manual' ? 'manual' : 'automatic'} power`

export const managedHostingProviderLabel = (provider: ManagedHostingProvider) =>
    provider === ManagedHostingProvider.aws ? 'AWS' : 'OCI'

/**
 * The catalog locations for a new control plane, or for a hosted node or video region of an existing one. A group
 * peers its private networks within one cloud provider, so its nodes and regions use the control plane's.
 */
export const hostingLocationsFor = (
    locations: readonly ManagedHostingLocation[],
    kind: ManagedHostingLocation['kind'],
    controlPlane?: Pick<ManagedHostingDeployment, 'location'>
) =>
    locations.filter(
        (location) => location.kind === kind && (!controlPlane || location.provider === controlPlane.location.provider)
    )

/** "AWS Stockholm · eu-north-1": catalog names already name the provider. */
export const managedHostingLocationLabel = (location: Pick<ManagedHostingLocation, 'name' | 'region'>) =>
    `${location.name} · ${location.region}`

export const managedHostingRoleLabel = (deployment: Pick<ManagedHostingDeployment, 'kind' | 'edgeRole'>) =>
    deployment.kind === 'core' ? 'Control plane' : deployment.edgeRole === 'core' ? 'Video region node' : 'Edge Connect'

/** "Edge Connect of Studio" for a hosted node, so a list row names the control plane it belongs to. */
export const managedHostingGroupRoleLabel = (
    deployment: Pick<ManagedHostingDeployment, 'kind' | 'edgeRole' | 'controlPlaneName'>
) =>
    deployment.kind === 'connect' && deployment.controlPlaneName
        ? `${managedHostingRoleLabel(deployment)} of ${deployment.controlPlaneName}`
        : managedHostingRoleLabel(deployment)

/**
 * The deployment was permanently deleted, so no action remains and lists hide it by default. Recoverable deletions
 * stay listed because members still restore or purge them.
 */
export const isPurgedHosting = (deployment: Pick<ManagedHostingDeployment, 'status'>) => deployment.status === deleted

/**
 * Whether the deployment's status and kind allow the action. Operations already in progress and the member's role
 * are checked separately.
 */
export const canRequestHostingAction = (
    deployment: Pick<ManagedHostingDeployment, 'kind' | 'status'>,
    action: ManagedHostingAction
) =>
    managedHostingActionStatuses[action].includes(deployment.status) &&
    (kindActions[action] ?? deployment.kind) === deployment.kind

/** Deletion can be requested; it is unavailable once underway and while the worker restores or upgrades. */
export const canDeleteHosting = (deployment: Pick<ManagedHostingDeployment, 'status'>) =>
    !deletionStatuses.includes(deployment.status) &&
    !busyStatuses.includes(deployment.status) &&
    deployment.status !== purgeFailed

export const isHostingOperationActive = (operation: Pick<ManagedHostingOperation, 'status'>) =>
    operation.status === 'queued' || operation.status === 'running'

/** The worker's health check, which it queues for every running deployment about once a minute. */
export const isRoutineHostingCheck = (operation: Pick<ManagedHostingOperation, 'action' | 'scheduled'>) =>
    operation.scheduled && operation.action === 'reconcile'

/** Work that must finish before members request more; the worker runs routine checks between requests. */
export const blocksHostingActions = (operation: Pick<ManagedHostingOperation, 'action' | 'scheduled' | 'status'>) =>
    isHostingOperationActive(operation) && !isRoutineHostingCheck(operation)

/**
 * Whether Retry can be requested. A degraded deployment needs failed work to repeat, which the server marks
 * `retryable`; the other statuses that allow Retry are failures in themselves.
 * @param operations - The deployment's operations
 */
export const canRetryHosting = (
    deployment: Pick<ManagedHostingDeployment, 'kind' | 'status'>,
    operations: readonly Pick<ManagedHostingOperation, 'retryable'>[]
) =>
    canRequestHostingAction(deployment, 'retry') &&
    (deployment.status !== degraded || operations.some((operation) => operation.retryable))

/** Operation history worth showing: routine checks appear only when they fail. */
export const isNotableHostingOperation = (
    operation: Pick<ManagedHostingOperation, 'action' | 'scheduled' | 'status'>
) => !isRoutineHostingCheck(operation) || operation.status === 'failed'

/** Backups still retained, which members can download and restore. */
export const isRetainedHostingBackup = (backup: Pick<ManagedHostingBackup, 'expiresAt'>, now = Date.now()) =>
    new Date(backup.expiresAt).getTime() > now

/**
 * Whether the backup can be restored on the running Core. Edge restores a configuration only into the release that
 * took it; a backup from another release can still be downloaded.
 */
export const canRestoreHostingBackupInPlace = (
    deployment: Pick<ManagedHostingDeployment, 'location'>,
    backup: Pick<ManagedHostingBackup, 'version'>
) => backup.version === deployment.location.version

const releaseVersion = /^R(\d+)\.(\d+)\.(\d+)(?:-rc(\d+))?$/
const buildVersion = /^ci-(\d+)$/

/**
 * Orders two hosting releases: `R<major>.<minor>.<patch>` releases, where a final release follows its release
 * candidates (`-rc<n>`), or `ci-<pipeline>` builds by pipeline.
 * @returns A negative number, zero or a positive number as `a` is older than, the same as or newer than `b`;
 * undefined when only one is a CI build or either has another form, such as a hotfix
 */
export function compareHostingVersions(a: string, b: string): number | undefined {
    const [buildA, buildB] = [buildVersion.exec(a), buildVersion.exec(b)]
    if (buildA && buildB) return Math.sign(Number(buildA[1]) - Number(buildB[1]))
    const [releaseA, releaseB] = [releaseVersion.exec(a), releaseVersion.exec(b)]
    if (!releaseA || !releaseB) return undefined
    for (const part of [1, 2, 3]) {
        const difference = Number(releaseA[part]) - Number(releaseB[part])
        if (difference) return Math.sign(difference)
    }
    const [candidateA, candidateB] = [releaseA[4], releaseB[4]].map((value) =>
        value === undefined ? Infinity : Number(value)
    )
    return candidateA === candidateB ? 0 : candidateA > candidateB ? 1 : -1
}

/** Upgrades only move forward: a release that cannot be ordered against the current one is never a target. */
export const isNewerHostingVersion = (candidate: string, current: string) =>
    (compareHostingVersions(candidate, current) ?? 0) > 0

/** Newer catalog releases the deployment can be upgraded to in place: same kind and cloud location, with a bundle. */
export const hostingUpgradeTargets = (
    deployment: Pick<ManagedHostingDeployment, 'kind' | 'location'>,
    locations: readonly ManagedHostingLocation[]
) =>
    locations.filter(
        (location) =>
            location.kind === deployment.kind &&
            location.upgradeAvailable === true &&
            location.provider === deployment.location.provider &&
            location.region === deployment.location.region &&
            isNewerHostingVersion(location.version, deployment.location.version)
    )

/** Browser address of the deployment's own UI: Network Manager for a Core, the proxied appliance UI for a Connect. */
export const hostingManagementUrl = (
    deployment: Pick<ManagedHostingDeployment, 'kind' | 'hostname' | 'controlPlaneHostname' | 'applianceId'>
) =>
    deployment.kind === 'core'
        ? `https://${deployment.hostname}`
        : deployment.controlPlaneHostname && deployment.applianceId
          ? `https://${deployment.controlPlaneHostname}/api/appliance/${deployment.applianceId}/proxy/`
          : undefined
